import { PLAN_PRESENTATION, PLANS } from "@mfa/v5-contracts";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { useAppContext } from "../../app/AppContext";
import { useBillingAccount } from "../../core/billing-hooks";
import { sharedStyles } from "../../shared/Shared";
import pageStyles from "../../styles/Page.module.css";
import { ApiKeysSection } from "./ApiKeysSection";
import styles from "./ProfilePage.module.css";

// The avatar tint (V1's default robot accent).
const accent = "#7C5CFF";

/**
 * Owner account page (ported from V1 "My Profile"): identity, plan, subscription actions,
 * sign out. The plan is the one `billing.account` reports (the same snapshot every billing
 * surface renders from); until that answers — or if it fails — the line names the site's
 * default plan from the constants, which is what every new account is on. The agent list
 * returns with projects in Section 2. Below the plan, the account's API keys on this site
 * (ApiKeysSection).
 */
export function ProfilePage() {
  const navigate = useNavigate();
  const { viewer, experience, locale, signOut } = useAppContext();
  const ownerName = viewer?.displayName ?? experience.terminology.owner;
  const billingQuery = useBillingAccount();
  const defaultPlan = PLANS.find((plan) => plan.siteId === experience.id && plan.isDefault);
  const defaultPlanName = defaultPlan ? PLAN_PRESENTATION[locale][defaultPlan.id].name : null;
  // Server-resolved plan NAME, never a raw id; the constants only stand in while it loads.
  const planName = billingQuery.data?.account.plan.name ?? defaultPlanName;

  return (
    <div className={pageStyles.page}>
      <header className={pageStyles.pageHeader}>
        <div>
          <p className={pageStyles.eyebrow}>{experience.brand.name}</p>
          <h1 className={pageStyles.title}>
            <FormattedMessage id="profile.myProfile" />
          </h1>
        </div>
      </header>

      <div className={styles.columns}>
        {/* Identity card */}
        <aside className={pageStyles.card}>
          <div
            aria-hidden="true"
            style={{
              display: "grid",
              placeItems: "center",
              width: 96,
              height: 96,
              borderRadius: 20,
              fontSize: 52,
              background: `color-mix(in srgb, ${accent} 22%, transparent)`,
              border: `var(--brand-bw-3) solid ${accent}`,
            }}
          >
            🤖
          </div>
          <h2 style={{ marginTop: 14 }}>{ownerName}</h2>
          {viewer?.email ? <p style={{ opacity: 0.75 }}>{viewer.email}</p> : null}
          {planName ? <span className={pageStyles.badge}>{planName}</span> : null}

          <div className={styles.actions}>
            <Link className={sharedStyles.button} to="/templates">
              <FormattedMessage id="profile.openTemplates" />
            </Link>
            <button
              className={`${sharedStyles.button} ${sharedStyles.buttonSecondary}`}
              type="button"
              onClick={() => void signOut().then(() => navigate("/"))}
            >
              <FormattedMessage id="auth.signOut" />
            </button>
          </div>
        </aside>

        {/* Subscription, then the account's API keys */}
        <div className={styles.stack}>
          <section className={pageStyles.card}>
            <h2 className={pageStyles.sectionTitle}>
              <FormattedMessage id="profile.subscription" />
            </h2>
            <p style={{ marginTop: 10 }}>
              {planName ? (
                <FormattedMessage id="profile.currentPlan" values={{ plan: planName }} />
              ) : null}
            </p>
            <Link
              className={sharedStyles.button}
              to="/billing"
              style={{ marginTop: 12, display: "inline-block" }}
            >
              <FormattedMessage id="profile.managePlan" />
            </Link>
          </section>

          <ApiKeysSection />
        </div>
      </div>
    </div>
  );
}
